import React from 'react';
import { useLocation } from 'react-router-dom';
import { SeoHead } from '../components/SeoHead';
import { getStaticPageMetadata } from '../lib/seo';
// buildDirectoryUrl rather than a hand-rolled `/${state}/${slug}/gyms`: hub-locations.json
// stores the BARE slug, so a literal template here would link all 190 A–Z entries at the
// pre-O6 address and take every one of them through a 301.
import { toDisplaySuburb, buildDirectoryUrl } from '../lib/slugs';
import { DEFAULT_STATE, LAUNCHED_STATES, stateSlugToDisplay, buildStateHubUrl } from '../lib/stateCore.mjs';
import type { StateSlug } from '../lib/stateCore.mjs';
import { PageContainer } from '../src/components/ui/page-container';
import Breadcrumb from '../src/components/ui/breadcrumb';
import { generateHubBreadcrumbs } from '../lib/breadcrumbs';
import StateHubNav from '../src/components/StateHubNav';
import hubLocationsData from '../data/hub-locations.json';
/**
 * O8: the A–Z lists EVERY suburb with at least one gym (601), not just the 190 that
 * clear the indexable threshold. The thin ones are real, reachable, searchable pages
 * that simply appeared nowhere on the site.
 *
 * Read from its own file rather than from hub-locations.json, which stays the ≥3-gym
 * set because scripts/blog-tag-vocab.mjs treats it as the canonical blog REGION
 * vocabulary and lib/seo.hubCount.test.ts locks its length. Widening that file would
 * silently redefine which blog tags are valid regions.
 */
import allLocationsData from '../data/hub-locations-all.json';

/**
 * NSW Gyms Hub Page — /nsw/gyms
 *
 * Static SSG hub that pre-renders a native-HTML anchor link to every NSW
 * suburb directory page that passes MIN_GYMS_PER_LOCATION. Exists purely
 * for SEO: it gives Google a static internal-link graph into the location
 * pages, which are otherwise reachable only via sitemap.
 *
 * Suburb links MUST be plain <a href>, not react-router <Link>. The static
 * HTML crawled by Googlebot needs literal <a> tags — <Link> renders via JS
 * and would not appear in the pre-rendered HTML.
 */

interface HubLocation {
    slug: string;
    suburb: string;
    count: number;
}

// R4: data/hub-locations.json is STATE-KEYED ({ nsw: [...] }); each hub route
// reads its own state's slice. NSW content is identical to the pre-R4 array.
const hubLocationsByState = hubLocationsData as Record<string, HubLocation[]>;
const allLocationsByState = allLocationsData as Record<string, HubLocation[]>;

/**
 * Below this a suburb page is deliberately kept out of search (the thin-content rule),
 * so the A–Z still lists it but marks it as secondary. Mirrors MIN_GYMS_FOR_INDEX in
 * lib/seo.ts and MIN_GYMS_PER_LOCATION in the generators — all three must agree on 3.
 */
const MIN_GYMS_PROMINENT = 3;

/** First path segment when it is a launched state slug; else the default. */
function stateFromPathname(pathname: string): StateSlug {
    const first = pathname.split('/')[1] ?? '';
    return (LAUNCHED_STATES as readonly string[]).includes(first)
        ? (first as StateSlug)
        : DEFAULT_STATE;
}

interface LetterGroup {
    letter: string;
    suburbs: HubLocation[];
}

function groupByLetter(locations: HubLocation[]): LetterGroup[] {
    const groups = new Map<string, HubLocation[]>();
    for (const loc of locations) {
        const letter = loc.slug.charAt(0).toUpperCase();
        if (!groups.has(letter)) groups.set(letter, []);
        groups.get(letter)!.push(loc);
    }
    return Array.from(groups.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([letter, suburbs]) => ({ letter, suburbs }));
}

const NswGymsHubPage: React.FC = () => {
    const location = useLocation();
    const stateSlug = stateFromPathname(location.pathname);
    // Prefer the full list; fall back to the ≥3-gym set if the generated file is
    // missing, so a stale checkout renders the old page rather than an empty one.
    const hubLocations = allLocationsByState[stateSlug] ?? hubLocationsByState[stateSlug] ?? [];
    const groups = groupByLetter(hubLocations);
    const totalSuburbs = hubLocations.length;
    // Route-keyed, state-exact, DERIVED from buildStateHubUrl — never retyped
    // (review-caught REAL, 2026-08-14: the retyped `/${stateSlug}/gyms` key kept
    // "existing" after the UI-57 re-key, but the entry it found had become the
    // state DIRECTORY's — a truthy lookup for the WRONG page, the exact failure
    // shape the no-cross-state-fallback note below exists to prevent). null when
    // this state has no STATIC_PAGE_META entry — see the SeoHead call below.
    const hubMeta = getStaticPageMetadata(buildStateHubUrl(stateSlug));

    const handleLetterClick = (e: React.MouseEvent<HTMLAnchorElement>, letter: string) => {
        e.preventDefault();
        const target = document.getElementById(`letter-${letter}`);
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Move keyboard/SR focus to the section heading so the reading position
        // follows the scroll (WCAG 2.4.3). preventScroll keeps the smooth scroll
        // above from being interrupted by the focus jump. The <h2> is tabIndex={-1}.
        target.focus({ preventScroll: true });
        window.history.replaceState(null, '', `#letter-${letter}`);
    };

    return (
        <div className="min-h-screen bg-surface-canvas flex flex-col">
            {/* Route-keyed meta, with NO cross-state fallback.
                The former `?? getStaticPageMetadata(`/${DEFAULT_STATE}/gyms`)` looked
                like a safe default and was not: on a state with no STATIC_PAGE_META
                entry it served that state's page NSW's title, NSW's description and —
                the part that actually costs — NSW's canonicalPath, i.e. a self-
                referencing canonical pointing at a DIFFERENT state's URL. Nothing
                errors, nothing looks wrong, and the page simply never indexes.
                A missing entry now yields NO metadata rather than another state's.
                getStaticPageMetadata already returns null on a lookup miss;
                this stops that null being converted back into an inherited one.
                For /nsw/suburbs (the hub's address since UI-57) the entry exists,
                so `hubMeta` is truthy and SeoHead receives the hub's own identity —
                and the key is DERIVED, so it moves with any future relocation. */}
            {hubMeta && <SeoHead meta={hubMeta} />}

            <div className="h-header flex-shrink-0" />

            <main id="main-content" className="flex-grow">
                <PageContainer size="content" className="py-12">
                    {/* Visible trail from the shared spine (lib/breadcrumbs.ts) — the
                        same source the SSG BreadcrumbList derives from, so visible and
                        structured data cannot drift. Renders 'Home › NSW Suburbs': the
                        former middle 'Gyms' → /gyms crumb was dropped (owner decision
                        2026-07-25) because /gyms is the search/map tool, not a URL
                        ancestor of this page. */}
                    <Breadcrumb items={generateHubBreadcrumbs(stateSlug)} />

                    {/* The state token is derived from the same `stateSlug` the rest of
                        this page already uses (metadata, breadcrumbs, directory hrefs),
                        via stateSlugToDisplay — a pure uppercase mapping. For /nsw/gyms
                        this renders 'NSW', byte-identical to the former literal. */}
                    <h1 className="text-3xl md:text-4xl font-semibold text-brand-primary mb-3">
                        Martial Arts Gyms in {stateSlugToDisplay(stateSlug)} — Browse by Suburb
                    </h1>
                    <p className="text-text-muted leading-relaxed mb-8">
                        Explore {totalSuburbs} {stateSlugToDisplay(stateSlug)} suburbs with listed martial arts gyms. Each page shows the local gyms along with the styles they offer.
                    </p>

                    {/* Sibling navigation between the state hubs. Renders NOTHING while only
                        one state is launched — see src/components/StateHubNav.tsx. Placed
                        after the intro and before the letter nav so the page reads
                        heading → context → which state → the A–Z itself. */}
                    <StateHubNav activeState={stateSlug} />

                    {groups.length > 0 && (
                        <nav aria-label="Jump to letter" className="mb-10">
                            {/*
                              * 2.5rem, not 2.75rem — the MINIMUM is what decides the wrap, not the
                              * rendered size. The alphabet grew from 22 letters to 25 when the hub
                              * started listing all 601 suburbs (Q, V and Y appeared), and at this
                              * page's 1280px container a 2.75rem minimum fits only 23 cells, so the
                              * row broke onto two lines. At 2.5rem all 25 fit; the cells still render
                              * 41.6px wide, so nothing shrinks visually.
                              *
                              * Deliberately still WRAPS on narrow screens. Forcing 25 onto one line at
                              * phone width gives ~13px targets, well under the accessible minimum —
                              * the h-11 (44px) height exists for the same reason.
                              */}
                            <ul className="grid grid-cols-[repeat(auto-fit,minmax(2.5rem,1fr))] gap-2">
                                {groups.map(g => (
                                    <li key={g.letter} className="flex">
                                        <a
                                            href={`#letter-${g.letter}`}
                                            onClick={(e) => handleLetterClick(e, g.letter)}
                                            className="flex items-center justify-center w-full h-11 rounded-md bg-surface-canvas border border-border-default text-text-primary hover:border-text-subtle transition-colors duration-150 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary"
                                        >
                                            {g.letter}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    )}

                    <div>
                        {groups.map((group, idx) => (
                            <React.Fragment key={group.letter}>
                                <h2
                                    id={`letter-${group.letter}`}
                                    tabIndex={-1}
                                    className={`text-2xl font-semibold text-text-primary pb-2 border-b border-border-default focus:outline-none ${idx > 0 ? 'mt-8' : ''} mb-2`}
                                    style={{ scrollMarginTop: '5rem' }}
                                >
                                    {group.letter}
                                </h2>
                                {/*
                                  * auto-FILL, not auto-FIT (audit O8). The two differ only when a
                                  * row is not full, which is precisely this page's common case:
                                  * seven letters have three suburbs or fewer (I, J and Z have one).
                                  * `auto-fit` COLLAPSES the empty tracks and the `1fr` then stretches
                                  * the surviving items across the whole row, so "F" spread its three
                                  * suburbs edge to edge while "B" — with 24 — sat in tidy columns.
                                  * `auto-fill` keeps the empty tracks, so every letter's suburbs line
                                  * up on the same column grid regardless of how many it has.
                                  */}
                                <ul role="list" className="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] lg:grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-x-6 gap-y-2">
                                    {group.suburbs.map(loc => (
                                        <li key={loc.slug}>
                                            {/*
                                              * O8: suburbs below the threshold are listed but SECONDARY —
                                              * muted rather than brand-coloured, so the eye still lands on
                                              * the substantial suburbs. They are ordinary links: the pages
                                              * work, they are searchable, and they were simply invisible
                                              * from the site before this.
                                              *
                                              * NOT marked rel="nofollow". These pages carry `noindex,follow`,
                                              * which asks Google not to LIST them while still following
                                              * their outbound links to gym profiles — nofollow here would
                                              * cut that path and strand link equity that currently flows to
                                              * real, indexable gym pages.
                                              */}
                                            <a
                                                href={buildDirectoryUrl(loc.slug, stateSlug)}
                                                className={
                                                    loc.count >= MIN_GYMS_PROMINENT
                                                        ? 'text-brand-primary hover:text-brand-primary-hover hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary'
                                                        : 'text-text-muted hover:text-text-primary hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary'
                                                }
                                            >
                                                {toDisplaySuburb(loc.slug)}
                                            </a>
                                            {/* UI-07: text-text-muted, not text-text-subtle — subtle (#8A9199) is
                                                2.97:1 on surface-canvas and fails WCAG AA for normal text; muted
                                                (#656D76) passes. The subtle token itself is untouched: it is also
                                                used on dark surfaces (Header/drawer), where darkening it would
                                                hurt contrast the other way. */}
                                            <span className="text-text-muted text-sm ml-1">({loc.count})</span>
                                        </li>
                                    ))}
                                </ul>
                            </React.Fragment>
                        ))}
                    </div>
                </PageContainer>
            </main>

        </div>
    );
};

export default NswGymsHubPage;
